import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { getAllFlags, updateFlagStatus, deleteFlag } from '../../../services/pirateGalleryService';

// ===========================================================================
// Styled components
// ===========================================================================

const Wrapper = styled.div``;

const LoadingMsg = styled.p`
  color: #888;
  font-size: 0.95rem;
  padding: 2rem 0;
  text-align: center;
`;

const ErrorMsg = styled.div`
  background: #fef2f2;
  border: 1px solid #fca5a5;
  border-radius: 10px;
  padding: 1rem 1.25rem;
  color: #dc2626;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const RetryBtn = styled.button`
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.4rem 0.875rem;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { background: #b91c1c; }
`;

const TabBar = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.75rem;
  background: ${props => props.theme.colors.offWhite};
  border-radius: 12px;
  padding: 0.4rem;
`;

const Tab = styled.button`
  flex: 1;
  padding: 0.65rem 1rem;
  border-radius: 9px;
  border: none;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.active ? 'white' : 'transparent'};
  color: ${props => props.active ? props.theme.colors.primary : '#888'};
  box-shadow: ${props => props.active ? '0 2px 6px rgba(0,0,0,0.08)' : 'none'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
`;

const Badge = styled.span`
  background: ${props => {
    if (props.variant === 'pending')  return '#fbbf24';
    if (props.variant === 'approved') return '#22c55e';
    if (props.variant === 'rejected') return '#ef4444';
    return '#e5e7eb';
  }};
  color: ${props => {
    if (props.variant === 'pending')  return '#78350f';
    if (props.variant === 'approved') return '#14532d';
    if (props.variant === 'rejected') return '#7f1d1d';
    return '#374151';
  }};
  border-radius: 50px;
  padding: 0.1rem 0.55rem;
  font-size: 0.75rem;
  font-weight: 700;
  min-width: 20px;
  text-align: center;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3.5rem 1rem;
  color: #aaa;
`;

const EmptyEmoji = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const EmptyText = styled.p`
  font-size: 1rem;
  margin: 0;
`;

const SubmissionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const SubmissionCard = styled.div`
  border: 2px solid ${props => {
    if (props.status === 'approved') return 'rgba(34, 197, 94, 0.3)';
    if (props.status === 'rejected') return 'rgba(239, 68, 68, 0.25)';
    return 'rgba(16, 29, 64, 0.1)';
  }};
  border-radius: 16px;
  overflow: hidden;
  background: white;
  transition: box-shadow 0.2s ease;
  opacity: ${props => props.busy ? 0.6 : 1};
  pointer-events: ${props => props.busy ? 'none' : 'auto'};

  &:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.08); }
`;

const CardTop = styled.div`
  display: flex;
  @media (max-width: 600px) { flex-direction: column; }
`;

const Thumbnail = styled.div`
  width: 160px;
  flex-shrink: 0;
  background: #f5f0e8;
  cursor: pointer;
  overflow: hidden;
  position: relative;
  @media (max-width: 600px) { width: 100%; height: 200px; }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  &:hover img { transform: scale(1.06); }
`;

const ThumbnailOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
  &::after {
    content: '🔍';
    font-size: 1.75rem;
    opacity: 0;
    transition: opacity 0.2s ease;
  }
  ${Thumbnail}:hover & {
    background: rgba(0,0,0,0.25);
    &::after { opacity: 1; }
  }
`;

const CardInfo = styled.div`
  flex: 1;
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 0.75rem;
`;

const InfoTop = styled.div``;

const ChildTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.25rem;
`;

const ChildMeta = styled.p`
  color: ${props => props.theme.colors.accent};
  font-weight: 600;
  font-size: 0.875rem;
  margin: 0 0 0.5rem;
`;

const ChildSchool = styled.p`
  margin: 0 0 0.35rem;
  font-size: 0.82rem;
  color: #555;
`;

const ChildEmail = styled.p`
  margin: 0 0 0.5rem;
  font-size: 0.82rem;

  a {
    color: ${props => props.theme.colors.primary};
    word-break: break-word;
  }
`;

const ChildDescription = styled.p`
  color: #666;
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.5;
`;

const MetaFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const SubmittedAt = styled.span`
  color: #bbb;
  font-size: 0.8rem;
`;

const StatusPill = styled.span`
  background: ${props => {
    if (props.status === 'approved') return 'rgba(34, 197, 94, 0.12)';
    if (props.status === 'rejected') return 'rgba(239, 68, 68, 0.1)';
    return 'rgba(251, 191, 36, 0.15)';
  }};
  color: ${props => {
    if (props.status === 'approved') return '#15803d';
    if (props.status === 'rejected') return '#dc2626';
    return '#b45309';
  }};
  border-radius: 50px;
  padding: 0.25rem 0.75rem;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.625rem;
  padding: 0.875rem 1.5rem;
  background: ${props => props.theme.colors.offWhite};
  border-top: 1px solid rgba(16, 29, 64, 0.06);
  flex-wrap: wrap;
`;

const ActionBtn = styled.button`
  padding: 0.5rem 1.1rem;
  border-radius: 9px;
  border: 2px solid transparent;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.35rem;

  &.approve {
    background: rgba(34, 197, 94, 0.1); color: #15803d;
    border-color: rgba(34, 197, 94, 0.3);
    &:hover { background: #22c55e; color: white; border-color: #22c55e; }
    &:disabled { opacity: 0.4; cursor: default; }
  }
  &.reject {
    background: rgba(239, 68, 68, 0.08); color: #dc2626;
    border-color: rgba(239, 68, 68, 0.25);
    &:hover { background: #ef4444; color: white; border-color: #ef4444; }
    &:disabled { opacity: 0.4; cursor: default; }
  }
  &.pending {
    background: rgba(251, 191, 36, 0.1); color: #b45309;
    border-color: rgba(251, 191, 36, 0.3);
    &:hover { background: #fbbf24; color: white; border-color: #fbbf24; }
  }
  &.delete {
    background: transparent; color: #aaa;
    border-color: #e5e7eb;
    margin-left: auto;
    &:hover { background: #fee2e2; color: #dc2626; border-color: #fca5a5; }
  }
`;

const Lightbox = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(10, 10, 20, 0.92);
  backdrop-filter: blur(8px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const LightboxContent = styled.div`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  max-width: 750px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 80px rgba(0,0,0,0.6);
`;

const LightboxImage = styled.img`
  width: 100%;
  max-height: 55vh;
  object-fit: contain;
  background: #f5f0e8;
`;

const LightboxClose = styled.button`
  position: absolute;
  top: 1rem; right: 1rem;
  width: 40px; height: 40px;
  border-radius: 50%;
  background: rgba(255,255,255,0.15);
  border: 2px solid rgba(255,255,255,0.3);
  color: white; font-size: 1rem;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.2s ease; z-index: 10000;
  &:hover { background: rgba(255,255,255,0.3); }
`;

const LightboxInfo = styled.div`
  padding: 1.25rem 1.5rem 1.75rem;
  overflow-y: auto;
`;

const GalleryLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: ${props => props.theme.colors.accent};
  font-weight: 600;
  font-size: 0.875rem;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const formatDate = iso =>
  new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

const TABS = ['pending', 'approved', 'rejected'];

// ===========================================================================
// Component
// ===========================================================================

const ChildrensArtModeration = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [activeTab, setActiveTab]     = useState('pending');
  const [busyId, setBusyId]           = useState(null);
  const [lightbox, setLightbox]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllFlags();
      setSubmissions(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const byStatus = status => submissions.filter(s => s.status === status);

  const handleStatus = async (id, status) => {
    setBusyId(id);
    try {
      await updateFlagStatus(id, status);
      setSubmissions(prev =>
        prev.map(s =>
          s.id === id
            ? { ...s, status, reviewedAt: new Date().toISOString() }
            : s
        )
      );
    } catch (err) {
      alert('Could not update status: ' + err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Permanently delete this submission? This cannot be undone.')) return;
    setBusyId(id);
    try {
      await deleteFlag(id);
      setSubmissions(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      alert('Could not delete: ' + err.message);
    } finally {
      setBusyId(null);
    }
  };

  const tabLabel = t => {
    const icons = { pending: '⏳', approved: '✅', rejected: '❌' };
    const count = byStatus(t).length;
    return (
      <>
        {icons[t]} {t.charAt(0).toUpperCase() + t.slice(1)}
        <Badge variant={t}>{count}</Badge>
      </>
    );
  };

  const current = byStatus(activeTab);

  return (
    <Wrapper>
      {/* Summary + links */}
      <div style={{ marginBottom: '1.5rem' }}>
        <p style={{ margin: '0 0 0.25rem', color: '#555', fontSize: '0.95rem' }}>
          Review children's pirate sail submissions. Approve entries to publish them on the public gallery page.
        </p>
        {!loading && !error && (
          <p style={{ margin: 0, color: '#aaa', fontSize: '0.85rem' }}>
            Total: {submissions.length} · Pending: {byStatus('pending').length} · Approved: {byStatus('approved').length} · Rejected: {byStatus('rejected').length}
          </p>
        )}
      </div>

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <GalleryLink href="/childrens-art-gallery" target="_blank" rel="noopener noreferrer">
          🖼️ Open Public Gallery
        </GalleryLink>
        <GalleryLink href="/childrens-art-gallery/submit" target="_blank" rel="noopener noreferrer">
          🏴‍☠️ Open Submission Page
        </GalleryLink>
      </div>

      {/* Error banner */}
      {error && (
        <ErrorMsg>
          <span>⚠️ {error}</span>
          <RetryBtn onClick={load}>Retry</RetryBtn>
        </ErrorMsg>
      )}

      {/* Loading */}
      {loading && <LoadingMsg>Loading submissions…</LoadingMsg>}

      {!loading && !error && (
        <>
          <TabBar>
            {TABS.map(t => (
              <Tab key={t} active={activeTab === t} onClick={() => setActiveTab(t)}>
                {tabLabel(t)}
              </Tab>
            ))}
          </TabBar>

          {current.length === 0 ? (
            <EmptyState>
              <EmptyEmoji>
                {activeTab === 'pending' ? '📭' : activeTab === 'approved' ? '🖼️' : '🗑️'}
              </EmptyEmoji>
              <EmptyText>
                {activeTab === 'pending' && 'No pending submissions right now.'}
                {activeTab === 'approved' && 'No approved designs yet.'}
                {activeTab === 'rejected' && 'No rejected submissions.'}
              </EmptyText>
            </EmptyState>
          ) : (
            <SubmissionList>
              {current.map(item => (
                <SubmissionCard key={item.id} status={item.status} busy={busyId === item.id}>
                  <CardTop>
                    <Thumbnail onClick={() => setLightbox(item)}>
                      <img src={item.imageUrl} alt={item.title} />
                      <ThumbnailOverlay />
                    </Thumbnail>
                    <CardInfo>
                      <InfoTop>
                        <ChildTitle>"{item.title}"</ChildTitle>
                        <ChildMeta>by {item.childName}, age {item.age}</ChildMeta>
                        {item.school && (
                          <ChildSchool>{item.school}</ChildSchool>
                        )}
                        {item.email && (
                          <ChildEmail>
                            <a href={`mailto:${item.email}`}>{item.email}</a>
                          </ChildEmail>
                        )}
                        {item.description && (
                          <ChildDescription>{item.description}</ChildDescription>
                        )}
                      </InfoTop>
                      <MetaFooter>
                        <SubmittedAt>Submitted {formatDate(item.submittedAt)}</SubmittedAt>
                        <StatusPill status={item.status}>{item.status}</StatusPill>
                      </MetaFooter>
                    </CardInfo>
                  </CardTop>
                  <Actions>
                    <ActionBtn
                      className="approve"
                      disabled={item.status === 'approved'}
                      onClick={() => handleStatus(item.id, 'approved')}
                    >
                      ✅ Approve
                    </ActionBtn>
                    <ActionBtn
                      className="reject"
                      disabled={item.status === 'rejected'}
                      onClick={() => handleStatus(item.id, 'rejected')}
                    >
                      ❌ Reject
                    </ActionBtn>
                    {item.status !== 'pending' && (
                      <ActionBtn
                        className="pending"
                        onClick={() => handleStatus(item.id, 'pending')}
                      >
                        ⏳ Move to Pending
                      </ActionBtn>
                    )}
                    <ActionBtn className="delete" onClick={() => handleDelete(item.id)}>
                      🗑️ Delete
                    </ActionBtn>
                  </Actions>
                </SubmissionCard>
              ))}
            </SubmissionList>
          )}
        </>
      )}

      {/* Full-size image lightbox */}
      {lightbox && (
        <Lightbox onClick={() => setLightbox(null)}>
          <LightboxClose onClick={() => setLightbox(null)}>✕</LightboxClose>
          <LightboxContent onClick={e => e.stopPropagation()}>
            <LightboxImage src={lightbox.imageUrl} alt={lightbox.title} />
            <LightboxInfo>
              <h3 style={{ margin: '0 0 0.25rem', color: '#101d40', fontSize: '1.5rem', fontFamily: "'Cormorant Garamond', serif" }}>
                "{lightbox.title}"
              </h3>
              <p style={{ margin: '0 0 0.5rem', color: '#9d6d49', fontWeight: 600, fontSize: '0.9rem' }}>
                by {lightbox.childName}, age {lightbox.age}
              </p>
              {lightbox.school && (
                <p style={{ margin: '0 0 0.35rem', color: '#555', fontSize: '0.85rem' }}>
                  {lightbox.school}
                </p>
              )}
              {lightbox.email && (
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.85rem' }}>
                  <a href={`mailto:${lightbox.email}`} style={{ color: '#101d40' }}>
                    {lightbox.email}
                  </a>
                </p>
              )}
              {lightbox.description && (
                <p style={{ margin: '0 0 0.75rem', color: '#555', lineHeight: 1.6, fontSize: '0.9rem' }}>
                  {lightbox.description}
                </p>
              )}
              <p style={{ margin: 0, color: '#bbb', fontSize: '0.8rem' }}>
                Submitted {formatDate(lightbox.submittedAt)}
              </p>
            </LightboxInfo>
          </LightboxContent>
        </Lightbox>
      )}
    </Wrapper>
  );
};

export default ChildrensArtModeration;
